/**
 * Logfire API Service
 *
 * Service functions for interacting with the Logfire integration API endpoints.
 */

import { apiClient } from "./api";
import type {
  LogfireTokenCreate,
  LogfireTokenCreateResponse,
  LogfireTokenValidation,
  PaginatedLogfireTokens,
} from "@/types/integrations";

const API_PREFIX = "/api/v1/logfire";

/**
 * Create a new Logfire token and validate it.
 */
export async function createLogfireToken(
  tokenData: LogfireTokenCreate
): Promise<LogfireTokenCreateResponse> {
  return apiClient.post<LogfireTokenCreateResponse>(`${API_PREFIX}/tokens`, tokenData);
}

/**
 * List all Logfire tokens for the current user with pagination.
 */
export async function listLogfireTokens(
  page: number = 1,
  itemsPerPage: number = 10
): Promise<PaginatedLogfireTokens> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("items_per_page", itemsPerPage.toString());

  const queryString = params.toString();
  const url = `${API_PREFIX}/tokens?${queryString}`;
  return apiClient.get<PaginatedLogfireTokens>(url);
}

/**
 * Delete a Logfire token by ID.
 */
export async function deleteLogfireToken(tokenId: string): Promise<void> {
  return apiClient.delete(`${API_PREFIX}/tokens/${tokenId}`);
}

/**
 * Re-validate an existing Logfire token.
 */
export async function validateLogfireToken(tokenId: string): Promise<LogfireTokenValidation> {
  return apiClient.post<LogfireTokenValidation>(`${API_PREFIX}/tokens/${tokenId}/validate`, {});
}
